import { useEffect, useRef, useState } from "react";
import Autosuggest from "react-autosuggest";
import { AutoCompleteSelectedValue, KeyValuePair } from "../../types/generic";
import { t } from "i18next";

interface AutoCompleteProps {
  /** Optional css class names for the text input */
  className?: string;
  /** The id attribute for the text input. Useful when pairing this control with a label */
  inputId?: string;
  /** Optional placeholder attribute to render inside the text input */
  placeholder?: string | undefined;
  selectedValue: string | undefined;
  /** Whether or not to raise the valueSelected event for custom typed values (i.e. not just ones in the suggestion list) */
  allowCustomTypedValues: boolean;
  /** Whether or not to disable the field */
  disabled?: boolean;
  /** The list of suggestions to pick from */
  valuesList: KeyValuePair<string, string>[];
  onValueSelected(newValue: AutoCompleteSelectedValue | null): void;
  handleCustomTypedTitle?: (customTitle: string) => void;
  /** Whether to display the key or the value of the selected item in the text input field once a selection has been made */
  suggestionPropertyToDisplay?: "KEY" | "VALUE";
  /** An optional custom method for the HTML to render for each suggestion */
  customSuggestionRender?(
    suggestion: KeyValuePair<string, string>
  ): React.ReactNode;
  /** If provided, instead of the standard searching for suggestions based on the input list provided, the logic for searching can be handled outside this component */
  customSearchFunction?:
    | undefined
    | ((searchValue: string) => KeyValuePair<string, string>[]);
  /** Async search (e.g. API-backed suggestions). Takes precedence over customSearchFunction when set. */
  asyncSearchFunction?: (
    searchValue: string
  ) => Promise<KeyValuePair<string, string>[]>;
  /** Called when the input loses focus */
  onInputBlur?: () => void;
}

interface AutoCompleteValueChangeEventProps {
  newValue: string;
  method: "type" | "click" | "enter" | "escape" | "up" | "down";
}

interface AutoCompleteSuggestionSelectedEventProps {
  suggestion: KeyValuePair<string, string>;
}

interface AutoCompleteSearchEventProps {
  value: string;
}

/** An autocomplete/search text field */
const AutoComplete = ({
  inputId = "",
  selectedValue = "",
  placeholder,
  valuesList,
  onValueSelected,
  handleCustomTypedTitle,
  className = "",
  allowCustomTypedValues = false,
  suggestionPropertyToDisplay = "VALUE",
  customSuggestionRender,
  customSearchFunction,
  asyncSearchFunction,
  onInputBlur,
  disabled = false,
}: AutoCompleteProps) => {
  const [displayText, setDisplayText] = useState<string>(selectedValue ?? "");
  const [suggestions, setSuggestions] = useState<
    KeyValuePair<string, string>[]
  >([]);
  const searchRequestIdRef = useRef(0);

  useEffect(() => {
    setDisplayText(selectedValue ?? "");
  }, [selectedValue]);

  // Get the suggestions for the search value
  const getSuggestions = (value: string) => {
    const inputValue = value.trim().toLowerCase();
    const inputLength = inputValue.length;

    if (inputLength === 0) {
      return [];
    } else if (customSearchFunction) {
      // If a custom search function is provided, run that instead
      return customSearchFunction(value);
    } else {
      // Do the standard search
      return valuesList.filter(
        (kvp) => kvp.value.toLowerCase().slice(0, inputLength) === inputValue
      );
    }
  };

  // Autosuggest will call this function every time you need to update suggestions.
  // You already implemented this logic above, so just use it.
  const onSuggestionsFetchRequested = ({
    value,
  }: AutoCompleteSearchEventProps) => {
    if (asyncSearchFunction) {
      const trimmed = value.trim();
      if (trimmed.length === 0) {
        ++searchRequestIdRef.current;
        setSuggestions([]);
        return;
      }

      const requestId = ++searchRequestIdRef.current;
      asyncSearchFunction(value)
        .then((results) => {
          if (requestId === searchRequestIdRef.current) {
            setSuggestions(results);
          }
        })
        .catch(() => {
          if (requestId === searchRequestIdRef.current) {
            setSuggestions([]);
          }
        });
      return;
    }

    setSuggestions(getSuggestions(value));
  };

  // Autosuggest will call this function every time you need to clear suggestions.
  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const onSuggestionSelected = (
    _event: React.FormEvent<unknown>,
    { suggestion }: AutoCompleteSuggestionSelectedEventProps
  ) => {
    onValueSelected({
      displayText: suggestion.value,
      isCustomValue: false,
      key: suggestion.key,
    });
  };

  const onInputValueChange = (
    _event: React.FormEvent<unknown>,
    { newValue, method }: AutoCompleteValueChangeEventProps
  ) => {
    setDisplayText(newValue);

    if (method !== "type") {
      return;
    }

    if (allowCustomTypedValues) {
      handleCustomTypedTitle?.(newValue);

      onValueSelected({
        displayText: newValue,
        isCustomValue: true,
        key: null,
      });
      return;
    }

    // Only raise the onChange event if the typed value exactly matches a list item
    let kvp: KeyValuePair<string, string> | undefined = undefined;
    if (suggestionPropertyToDisplay === "VALUE") {
      kvp = valuesList.find((x) => x.value === newValue);
    } else {
      kvp = valuesList.find((x) => x.key === newValue);
    }
    if (kvp) {
      onValueSelected({
        displayText: kvp.value,
        isCustomValue: false,
        key: kvp.key,
      });
    }
  };

  const inputProps = {
    id: inputId,
    autoComplete: "off",
    className: className,
    placeholder: placeholder,
    value: displayText,
    onChange: onInputValueChange,
    onBlur: onInputBlur,
    type: "search",
    disabled: disabled,
  };

  const defaultSuggestionRender = (
    suggestion: KeyValuePair<string, string>
  ) => (
    <div className="cursor-pointer px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
      {suggestion.value}
    </div>
  );

  return (
    <>
      <Autosuggest
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        onSuggestionSelected={onSuggestionSelected}
        getSuggestionValue={(suggestion) =>
          suggestionPropertyToDisplay === "VALUE"
            ? suggestion.value
            : suggestion.key
        }
        renderSuggestion={
          customSuggestionRender
            ? customSuggestionRender
            : defaultSuggestionRender
        }
        inputProps={inputProps}
      />
    </>
  );
};

export default AutoComplete;
